import { lazy } from 'react';
import { HomePage } from '../components/pages/HomePage';
import { Feed } from '../components/pages/FeedPage';
import { ChatPage } from '../components/pages/ChatPage';
import { BandsPage } from '../components/pages/BandsPage';
import SystemHealthPage from '../components/pages/SystemHealthPage';
import DiscoverPage from '../components/pages/DiscoverPage';
import {
    BANDS_TAB_ID,
    BILLING_TAB_ID,
    CHAT_TAB_ID,
    DISCOVER_TAB_ID,
    EVENTS_TAB_ID,
    FEED_TAB_ID,
    FRIENDS_TAB_ID,
    HOME_TAB_ID,
    MAIN_NAVIGATION_TAB_SPECS,
    SYSTEM_HEALTH_TAB_ID,
    VENUE_TAB_ID,
} from './MainNavigationTabSpecs';
import { prefetchDiscoverMap } from '../components/pages/DiscoverPage/prefetch';
import {
    DiscoverIcon,
    EventIcon,
    FeedIcon,
    BandsIcon,
    HealthIcon,
    HomeIcon,
    MessagesIcon,
    PeopleIcon,
    VenueIcon,
    BillingIcon,
} from '../components/atoms/Iconography/MainNavigationIcons.jsx';
import { hasAnyRole } from '../utils/roles';
import { isBillingUiEnabled } from './billingFeatures';

const FriendsPage = lazy(() => import('../components/pages/FriendsPage'));
const EventsPage = lazy(() => import('../components/pages/EventsPage'));
const VenuePage = lazy(() => import('../components/pages/VenuePage'));
const BillingPage = lazy(() => import('../components/pages/BillingPage'));

const TAB_COMPONENTS = Object.freeze({
    [HOME_TAB_ID]: HomePage,
    [FEED_TAB_ID]: Feed,
    [DISCOVER_TAB_ID]: DiscoverPage,
    [EVENTS_TAB_ID]: EventsPage,
    [VENUE_TAB_ID]: VenuePage,
    [FRIENDS_TAB_ID]: FriendsPage,
    [CHAT_TAB_ID]: ChatPage,
    [BANDS_TAB_ID]: BandsPage,
    [BILLING_TAB_ID]: BillingPage,
    [SYSTEM_HEALTH_TAB_ID]: SystemHealthPage,
});

const TAB_ICONS = Object.freeze({
    [HOME_TAB_ID]: HomeIcon,
    [FEED_TAB_ID]: FeedIcon,
    [DISCOVER_TAB_ID]: DiscoverIcon,
    [EVENTS_TAB_ID]: EventIcon,
    [VENUE_TAB_ID]: VenueIcon,
    [FRIENDS_TAB_ID]: PeopleIcon,
    [CHAT_TAB_ID]: MessagesIcon,
    [BANDS_TAB_ID]: BandsIcon,
    [BILLING_TAB_ID]: BillingIcon,
    [SYSTEM_HEALTH_TAB_ID]: HealthIcon,
});

const TAB_PREFETCH_HANDLERS = Object.freeze({
    [DISCOVER_TAB_ID]: prefetchDiscoverMap,
});

const PRIMARY_NAVIGATION_HIDDEN_TAB_IDS = Object.freeze(new Set([
    BILLING_TAB_ID,
]));

const TAB_DEFINITIONS = MAIN_NAVIGATION_TAB_SPECS.map((tab) => {
    const prefetchHandler = TAB_PREFETCH_HANDLERS[tab.id];
    return {
        ...tab,
        component: TAB_COMPONENTS[tab.id],
        icon: TAB_ICONS[tab.id],
        ...(prefetchHandler ? { onPrefetch: prefetchHandler } : {}),
    };
});

export const isPrimaryNavigationTab = (tab) => (
    Boolean(tab?.id && !PRIMARY_NAVIGATION_HIDDEN_TAB_IDS.has(tab.id))
);

export const getMainNavigationTabs = (roles = []) => {
    return TAB_DEFINITIONS.filter((tab) => {
        if (!isTabFeatureEnabled(tab.id)) {
            return false;
        }
        if (!tab.requiredRoles || tab.requiredRoles.length === 0) {
            return true;
        }
        return hasAnyRole(roles, tab.requiredRoles);
    });
};

export const getPrimaryNavigationTabs = (roles = []) => (
    getMainNavigationTabs(roles).filter(isPrimaryNavigationTab)
);

export const getTabDefinitionById = (tabId) => TAB_DEFINITIONS.find((tab) => tab.id === tabId && isTabFeatureEnabled(tab.id));

export const MAIN_NAVIGATION_TABS = TAB_DEFINITIONS;

function isTabFeatureEnabled(tabId) {
    if (tabId === BILLING_TAB_ID) {
        return isBillingUiEnabled();
    }
    return true;
}
